import type { ReactNode, SVGProps } from 'react'

export type IconName =
  | 'arrow'
  | 'bank'
  | 'calculator'
  | 'chart'
  | 'chevron'
  | 'clock'
  | 'document'
  | 'globe'
  | 'headset'
  | 'inspect'
  | 'lock'
  | 'mail'
  | 'monitor'
  | 'person'
  | 'personPlus'
  | 'pin'
  | 'search'
  | 'settings'
  | 'shield'
  | 'team'
  | 'users'

type IconProps = SVGProps<SVGSVGElement> & {
  name: IconName
}

const iconPaths: Record<IconName, ReactNode> = {
  arrow: (
    <>
      <path d="M5 12h13" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
  bank: (
    <>
      <path d="M3 9h18M5 9V7l7-4 7 4v2M6 9v9M10 9v9M14 9v9M18 9v9M3 18h18" />
    </>
  ),
  calculator: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 7h8M8 12h2M14 12h2M8 16h2M14 16h2" />
    </>
  ),
  chart: (
    <>
      <path d="m3 19 6-6 4 3 8-10" />
      <path d="M16 6h5v5" />
    </>
  ),
  chevron: <path d="m7 10 5 5 5-5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M12 9v3l2 2" />
    </>
  ),
  document: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5M9 12h6M9 16h6" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.2 2.5 3.4 5.5 3.4 9S14.2 18.5 12 21c-2.2-2.5-3.4-5.5-3.4-9S9.8 5.5 12 3Z" />
    </>
  ),
  headset: (
    <>
      <path d="M4 13v-2a8 8 0 0 1 16 0v2M4 13h3v7H5a2 2 0 0 1-2-2v-3a2 2 0 0 1 1-2ZM20 13h-3v7h2a2 2 0 0 0 2-2v-3a2 2 0 0 0-1-2Z" />
    </>
  ),
  inspect: (
    <>
      <circle cx="10" cy="10" r="6" />
      <path d="m15 15 6 6" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="5" width="18" height="12" rx="1.5" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 21c.7-4.6 3.2-7 7.5-7s6.8 2.4 7.5 7" />
    </>
  ),
  personPlus: (
    <>
      <circle cx="10" cy="8" r="3" />
      <path d="M4 20c.5-4.5 2.5-6.7 6-6.7s5.5 2.2 6 6.7M18 7v6M15 10h6" />
    </>
  ),
  pin: (
    <>
      <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5M8 11h5M10.5 8.5v5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="m19 13 2 1-2 4-2-1.1a7.5 7.5 0 0 1-2 1.1L15 21h-5l-.3-2.3a7.5 7.5 0 0 1-2.2-1.2L5 19l-2-4 2-1a8 8 0 0 1 0-2.5L3 10l2-4 2.2 1.2A7.5 7.5 0 0 1 10 6V3h4l.4 3a7.5 7.5 0 0 1 2.2 1.2L19 6l2 4-2 1.2a8 8 0 0 1 0 1.8Z" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 20 6v5c0 5.2-3.4 8.5-8 10-4.6-1.5-8-4.8-8-10V6l8-3Z" />
      <path d="m8.5 12 2.2 2.2 4.9-5" />
    </>
  ),
  team: (
    <>
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="8" r="3" />
      <path d="M2.5 20c.4-4.4 2.2-6.6 5.5-6.6 2 0 3.4.8 4.2 2.4.8-1.6 2.1-2.4 3.8-2.4 3.3 0 5.1 2.2 5.5 6.6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3 20c.4-4.3 2.4-6.5 6-6.5s5.6 2.2 6 6.5M15 15c3.4 0 5.3 1.7 5.7 5" />
    </>
  ),
}

export function Icon({ name, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {iconPaths[name]}
    </svg>
  )
}
